import Dexie from 'dexie';
import { AllUnitsEntity } from '../api/fetchMatchingLabVitals';
import { PadDForm } from '../api/pushVisit';
import { InventoryItem } from '../components/prescriptionPad/rightPanel/workspace/PrescribeAutoSuggestV2';
import {
    AdvicesEntity,
    DiagnosisEntityOrSymptomsEntity,
    LabTestsEntityOrProceduresEntity,
    ExaminationEntity,
    LabVitalEntity,
    MedicationsEntity,
    DentalExamination,
    DentalProcedure,
    InjectionsEntity,
    Procedure,
} from './types/prescription';
import { DoctorReferClinicAddress } from '../components/prescriptionPad/leftPanel/DoctorReferralSearchInput';
import { track } from '../tracking';
import { getActiveDoctorId } from './localstorage';
import { CareCanvas } from './types/CareCanvas';

export interface GenieItem {
    meta?: {
        updatedAt?: number;
        isLocal?: number; // 0 or 1
        fCount?: number; // used how many times
        do_not_purge?: boolean;
    };
}

export type GSymptomsDiagnosis = DiagnosisEntityOrSymptomsEntity & GenieItem;
export type GMedication = MedicationsEntity & GenieItem;
export type GInjection = InjectionsEntity & GenieItem;
export type GAdvice = AdvicesEntity & GenieItem;
export type GLabTest = LabTestsEntityOrProceduresEntity & GenieItem;
export type GExamination = {
    id: string;
    name: string;
} & GenieItem;
export type SectionNameConfig = { [key: string]: string };
export type GPTemplate = {
    id: string;
    name: string;
    diagnosis?: DiagnosisEntityOrSymptomsEntity[];
    symptoms?: DiagnosisEntityOrSymptomsEntity[];
    medications?: MedicationsEntity[];
    injections?: InjectionsEntity[];
    labTests?: LabTestsEntityOrProceduresEntity[];
    labVitals?: GLabVital[];
    examinations?: ExaminationEntity[];
    followup?: { msFromNow?: number; optionId?: string; notes?: string };
    advices?: AdvicesEntity[];
    dyform?: PadDForm[];
    isPackage?: boolean;
    procedures?: Procedure[];
    careCanvas?: CareCanvas[];
} & GenieItem;
export type GLTTemplate = {
    id: string;
    name: string;
    labTests: LabTestsEntityOrProceduresEntity[];
    isPackage?: boolean;
} & GenieItem;

export type GMedTemplate = {
    id: string;
    name: string;

    medications: MedicationsEntity[];
} & GenieItem;

export type GInjectionTemplate = {
    id: string;
    name: string;

    injections: InjectionsEntity[];
} & GenieItem;

export type GSymTemplate = {
    id: string;
    name: string;

    symptoms: DiagnosisEntityOrSymptomsEntity[];
} & GenieItem;

export type GDiaTemplate = {
    id: string;
    name: string;

    diagnosis: DiagnosisEntityOrSymptomsEntity[];
} & GenieItem;

export type GLabVitalTemplate = {
    id: string;
    name: string;

    labVitals: LabVitalEntity[];
} & GenieItem;

export type GExaminationsTemplate = {
    id: string;
    name: string;

    examinations: ExaminationEntity[];
} & GenieItem;

export type GProceduresTemplate = {
    id: string;
    name: string;
    procedures: Procedure[];
} & GenieItem;

export type GService = {
    id: string;
    name: string;
    price: number;
} & GenieItem;

export type GReferData = {
    s_name?: string;
    s_id?: string;
    id: string;
    doc: {
        id?: string;
        name?: {
            f: string;
            l: string;
        };
        phone?: {
            code: string;
            value: string;
        };
        clinic?: DoctorReferClinicAddress;
        s_name?: string;
        s_id?: string;
        note?: string;
        referred_doctor_url?: string;
        referred_doctor_oid?: string;
        type?: 'doctor' | 'speciality';
    };
};

export type GRefer = GReferData & GenieItem;

export type GDyForm = PadDForm & GenieItem;

export type GFollowUpData = {
    id: string;
    label: string;
    daysToAdd: number;
};
export type GFollowUp = GFollowUpData & GenieItem;

export type GVitals = {
    id: string;
    name: string;
    unit: string;
} & GenieItem;

export type GMedFrequency = { n: string } & { id: string } & GenieItem;
export type GMedSOA = { name: string; id: string } & GenieItem;
export type GMedTiming = { n: string } & { id: string } & GenieItem;
export type GLabVital = {
    id: string;
    name: string;
    unit_dislay_name?: string;
    unit?: AllUnitsEntity;
    all_units?: AllUnitsEntity[] | null;
    is_panel?: boolean;
};
export type GLabVitalEntity = GLabVital & GenieItem;

export interface GInventoryItem extends InventoryItem {
    id: string;
}

export type GDentalExamination = DentalExamination & GenieItem;

export type GDentalProcedure = DentalProcedure & GenieItem;

export type GDentalPastProcedure = DentalExamination & GenieItem;

export type GProcedure = Procedure & GenieItem;

export interface GTreatmentService {
    _id: string;
    service_id: string;
    name: string;
    price: number;
    created_at: string;
    updated_at: string;
    owner_id: string;
    clinic_id: string;
    archive: boolean;
}

export interface GTreatmentPackage {
    _id: string;
    package_id: string;
    name: string;
    price: number;
    created_at: string;
    updated_at: string;
    owner_id: string;
    clinic_id: string;
    services: GTreatmentService[];
    archive: boolean;
}

export const DB_TABLE_MISC = 'misc';
export const DB_TABLE_D_FORMS = 'dForms';
export const DB_TABLE_INVENTORY = 'inventory';
export const DB_TABLE_INVENTORY_V2 = 'inventoryV2';

const dbs: { [key: string]: Dexie } = {};

const getDb = (id: string): Dexie | undefined => {
    const dbId = id || getActiveDoctorId();
    if (!dbId) {
        return;
    }

    let db = dbs[dbId];

    if (db && db.isOpen()) {
        return db;
    } else {
        // close the db is a async call, for now commenting it out until we move to v2 of the db
        // try {
        //     db?.close();
        // } catch (e) {
        //     track('ERROR', { on: 'db not open, closing', why: (e as Error).message });
        // }
        // if (db) {
        // track('ERROR', {
        //     on: 'get db',
        //     why: 'db instance is not more valid',
        //     dbName: db?.name,
        //     reqDbName: dbName(dbId),
        //     idOpen: db?.isOpen().toString(),
        // });
        // }
    }

    try {
        db = new Dexie(dbName(dbId));
        dbs[dbId] = db;

        // on next version updrage pls remove the .upgrade() call
        db.version(58)
            .stores({
                visits: '&id,patient.id,patient.localId,prescriptionId,isCompleted',
                patients: '&localId,createdAt,updatedAt,personal.phone.n, followUpDate',
                activeOrderUpdates: '&id,updated_at',
                prescriptions: '&id,visitId, _is_failed',
                gSymptoms: '&name,meta.updatedAt,meta.isLocal,meta.fCount',
                gSymptomsV2: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gDiagnosis: '&name,meta.updatedAt,meta.isLocal,meta.fCount',
                gDiagnosisV2: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gMedications: '&name,meta.updatedAt,meta.isLocal,meta.fCount',
                gMedicationsV2: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gAdvices: '&text,meta.updatedAt,meta.isLocal,meta.fCount',
                gPTemplates: '&id,name,meta.updatedAt,meta.isLocal',
                gLTTemplates: '&id,name,meta.updatedAt,meta.isLocal',
                gMedTemplates: '&id,name,meta.updatedAt,meta.isLocal',
                gSymTemplates: '&id,name,meta.updatedAt,meta.isLocal',
                gDiaTemplates: '&id,name,meta.updatedAt,meta.isLocal',
                gLabVitalsTemplates: '&id,name,meta.updatedAt,meta.isLocal',
                gExaminationsTemplates: '&id,name,meta.updatedAt,meta.isLocal',
                gProceduresTemplates: '&id,name,meta.updatedAt,meta.isLocal',
                gVitals: '&id,meta.updatedAt,meta.isLocal',
                gLabTests: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gExaminations: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gRefer: '&id,s_id,meta.updatedAt,meta.isLocal,meta.fCount',
                gFollowUp: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gServices: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gMedFrequency: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gMedSOA: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gMedTiming: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gLabVitals: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gDentalExaminations: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gDentalProcedures: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gDentalPastProcedures: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gProcedures: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gMedicalRecordsTags: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                inventory: '&id',
                inventoryV2: '&id',
                snackingQuestions: '&q_id',
                gPadDyForm: '&id',
                milestones:
                    '&id,categoryId,name,ageGroup,maxHrsFromBirth,archived,created_at,updated_at',
                [DB_TABLE_MISC]: '&key',
                [DB_TABLE_D_FORMS]: '&key',
                profile: '&_id',
                todayAppointments: '&aid,[clinic_id+status]',
                appointments:
                    '&aid,full_date,[clinic_id+full_date],[clinic_id+full_date+status],is_local',
                businessUsers: '&oid',
                gTreatmentServices: '&_id,clinic_id',
                gTreatmentPackages: '&_id,clinic_id',
                gInjections: '&id,meta.updatedAt,meta.isLocal,meta.fCount',
                gInjectionTemplates: '&id,name,meta.updatedAt,meta.isLocal',
                doctors: '&_id',
                roles: '&id',
                doctorsInBiz: '&id',
            })
            .upgrade((trans) => {
                return trans
                    .table('patients')
                    .toCollection()
                    .modify((patient) => {
                        patient.updatedAt = patient.updatedAt || patient.createdAt;
                    });
            });

        return db;
    } catch (e) {
        console.log('Failed to get dbId while creating db', e);
    }
};

export default getDb;

function dbName(id: string): string {
    return `docapp${id ? `-${id}` : ''}`;
}
